import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, Download, Trash2, Lock, Upload } from 'lucide-react';
import { useApi } from '../auth/useApi';
import { DocumentViewer } from './DocumentViewer';
import { Eye } from 'lucide-react';

/* ============================================================================
 * TicketAttachments — list / upload / download / delete files on a ticket.
 * Reuses the private-blob + short-lived-SAS pattern from the KB image flow.
 * Internal-file visibility is enforced by the server; this UI only mirrors it.
 * ========================================================================== */

const C = {
  primary: 'var(--c-primary)', secondary: 'var(--c-secondary)', bg: 'var(--c-bg)', surface: 'var(--c-surface)',
  border: 'var(--c-border)', text: 'var(--c-text)', textMuted: 'var(--c-textMuted)', warn: 'var(--c-warn)', danger: 'var(--c-danger)',
};

export interface Attachment {
  AttachmentId: number;
  FileName: string;
  ContentType: string | null;
  SizeBytes: number;
  IsInternal: boolean;
  CreatedAt: string;
  UploadedByUserId: number;
  UploadedByName: string | null;
}

/** Upload one file to a ticket. Shared by this component and the New Ticket page. */
export async function uploadTicketFile(
  apiBase: string,
  getAccessToken: () => Promise<string>,
  ticketId: number | string,
  file: File,
  isInternal = false,
): Promise<void> {
  const fd = new FormData();
  fd.append('file', file);
  if (isInternal) fd.append('isInternal', 'true');
  const token = await getAccessToken();
  const res = await fetch(`${apiBase.replace(/\/$/, '')}/api/tickets/${ticketId}/attachments`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` }, // no Content-Type — browser sets multipart boundary
    body: fd,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Upload failed (${res.status}): ${detail.slice(0, 200)}`);
  }
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_BYTES = 25 * 1024 * 1024;

export const TicketAttachments: React.FC<{
  ticketId: number | string;
  canUpload: boolean;       // everyone except viewer
  canMarkInternal: boolean; // admin / tech only
}> = ({ ticketId, canUpload, canMarkInternal }) => {
  const api = useApi();
  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [markInternal, setMarkInternal] = useState(false);
  const [viewing, setViewing] = useState<Attachment | null>(null);

  // PDFs and images can render inline; other types only download.
  const viewerKind = (att: Attachment): 'pdf' | 'image' | null => {
    const ct = (att.ContentType || '').toLowerCase();
    if (ct === 'application/pdf' || /\.pdf$/i.test(att.FileName)) return 'pdf';
    if (ct.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(att.FileName)) return 'image';
    return null;
  };
  const viewUrl = (attId: number) => async () => {
    const r = await api.get<{ url: string }>(`/api/tickets/${ticketId}/attachments/${attId}/view`);
    return r.url;
  };
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    return api.get<{ attachments: Attachment[] }>(`/api/tickets/${ticketId}/attachments`)
      .then(r => setItems(r.attachments ?? []))
      .catch((e: any) => setError(e?.message ?? 'Failed to load attachments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (e.target) e.target.value = ''; // allow re-picking the same file
    if (files.length === 0) return;

    const tooBig = files.find(f => f.size > MAX_BYTES);
    if (tooBig) { setError(`"${tooBig.name}" is over the 25 MB limit.`); return; }

    setUploading(true);
    setError(null);
    try {
      for (const f of files) {
        await uploadTicketFile(api.apiBase, api.getAccessToken, ticketId, f, canMarkInternal && markInternal);
      }
      await load();
    } catch (e: any) {
      setError(e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const download = async (att: Attachment) => {
    try {
      const r = await api.get<{ url: string; fileName: string }>(
        `/api/tickets/${ticketId}/attachments/${att.AttachmentId}/download`
      );
      // SAS carries Content-Disposition: attachment, so this triggers a download.
      const a = document.createElement('a');
      a.href = r.url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      setError(e?.message ?? 'Download failed');
    }
  };

  const remove = async (att: Attachment) => {
    if (!window.confirm(`Delete "${att.FileName}"? This can't be undone.`)) return;
    try {
      await api.del(`/api/tickets/${ticketId}/attachments/${att.AttachmentId}`);
      await load();
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed');
    }
  };

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 18 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Paperclip size={14} /> Attachments ({items.length})
      </h3>

      {loading ? (
        <div style={{ color: C.textMuted, fontSize: 14 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ color: C.textMuted, fontSize: 14, fontStyle: 'italic' }}>No files attached yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(att => (
            <div key={att.AttachmentId} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              background: att.IsInternal ? 'var(--c-internal-bg)' : C.bg, border: `1px solid ${C.border}`, borderRadius: 6,
            }}>
              <Paperclip size={15} color={C.textMuted} style={{ flexShrink: 0 }} />
              <button onClick={() => download(att)} title="Download"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: C.primary, fontWeight: 600, fontSize: 14, textAlign: 'left', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {att.FileName}
              </button>
              {att.IsInternal && (
                <span title="Internal — hidden from the requester" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700, color: C.warn, flexShrink: 0 }}>
                  <Lock size={11} /> Internal
                </span>
              )}
              <span style={{ fontSize: 12, color: C.textMuted, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{fmtSize(att.SizeBytes)}</span>
              <span style={{ fontSize: 12, color: C.textMuted, flexShrink: 0 }} title={att.UploadedByName ?? ''}>
                {att.UploadedByName ?? 'Unknown'}
              </span>
              {viewerKind(att) && (
                <button onClick={() => setViewing(att)} title="View inline"
                  style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.textMuted, flexShrink: 0 }}>
                  <Eye size={15} />
                </button>
              )}
              <button onClick={() => download(att)} title="Download"
                style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.textMuted, flexShrink: 0 }}>
                <Download size={15} />
              </button>
              <button onClick={() => remove(att)} title="Delete"
                style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.danger, flexShrink: 0 }}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      {viewing && viewerKind(viewing) && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Viewing: {viewing.FileName}</span>
            <button onClick={() => setViewing(null)}
              style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12, color: C.textMuted }}>
              Close
            </button>
          </div>
          <DocumentViewer
            key={viewing.AttachmentId}
            getUrl={viewUrl(viewing.AttachmentId)}
            kind={viewerKind(viewing)!}
            fileName={viewing.FileName}
          />
        </div>
      )}

      {error && <div style={{ color: C.danger, fontSize: 13, marginTop: 10 }}>{error}</div>}

      {canUpload && (
        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" multiple onChange={onPick} style={{ display: 'none' }} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, background: uploading ? C.border : C.primary,
              color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 14, fontWeight: 600,
              cursor: uploading ? 'not-allowed' : 'pointer',
            }}>
            <Upload size={15} /> {uploading ? 'Uploading…' : 'Add files'}
          </button>
          {canMarkInternal && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.text, cursor: 'pointer' }}>
              <input type="checkbox" checked={markInternal} onChange={e => setMarkInternal(e.target.checked)} />
              Mark uploads internal (hidden from requester)
            </label>
          )}
          <span style={{ fontSize: 12, color: C.textMuted }}>Any file type · up to 25 MB each</span>
        </div>
      )}
    </div>
  );
};
